import { useState } from 'react';
import { Plus, Pencil, Trash2, KeyRound, Copy, Check, X, Settings, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';
import {
  useCountries,
  useDepartments,
  usePositions,
  useHolidays,
  useCreateHoliday,
  useDeleteHoliday,
  useCreateDepartment,
  useDeleteDepartment,
  useCreatePosition,
  useDeletePosition,
  useUsers,
  useUpdateUserRole,
  useResetUserPassword,
  usePolicies,
  useUpsertPolicy,
  useDeletePolicy,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';

type Section =
  | 'paises'
  | 'catalogs'
  | 'roles'
  | 'politicas';

const MENU = [
  { key: 'paises' as const, label: 'Países y regiones' },
  { key: 'catalogs' as const, label: 'Catálogos' },
  { key: 'roles' as const, label: 'Roles y permisos' },
  { key: 'politicas' as const, label: 'Políticas de vacaciones' },
];

export function ConfigPage() {
  const [section, setSection] = useState<Section>('paises');

  return (
    <PageTransition className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
          <Settings size={22} className="text-primary" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Configuración</h1>
          <p className="mt-0.5 text-sm text-font-secondary">
            Administra los parámetros globales del sistema
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Sidebar menu */}
        <div className="w-full rounded-xl border border-border bg-white p-2 shadow-sm lg:w-64 lg:flex-shrink-0">
          {MENU.map((m) => (
            <button
              key={m.key}
              onClick={() => setSection(m.key)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm font-semibold transition ${
                section === m.key
                  ? 'bg-primary-subtle text-primary'
                  : 'text-font-secondary hover:bg-bg'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <FadeIn key={section} delay={0.05}>
            {section === 'paises' && <CountriesSection />}
            {section === 'catalogs' && <CatalogsSection />}
            {section === 'roles' && <RolesSection />}
            {section === 'politicas' && <PoliciesSection />}
          </FadeIn>
        </div>
      </div>
    </PageTransition>
  );
}

function CountriesSection() {
  const { data } = useCountries();
  const year = new Date().getFullYear();
  const { data: holidays } = useHolidays(undefined, year);
  const [editing, setEditing] = useState<any | null>(null);

  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-font-primary">
            Países y Feriados
          </h2>
          <p className="mt-1 text-xs text-font-secondary">
            Administra los países donde ISOFT tiene operaciones y configura los
            feriados para cada uno (año {year}).
          </p>
        </div>
      </div>

      <div className="stagger space-y-3">
        {data?.items?.map((c: any) => {
          const hcount =
            holidays?.items?.filter((h: any) => h.countryId === c.id).length ?? 0;
          return (
            <div
              key={c.id}
              className="lift flex flex-wrap items-center gap-4 rounded-xl border border-border p-4 hover:border-primary"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-subtle text-xs font-bold text-primary">
                {c.code}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-font-primary">{c.nameEs}</p>
                <p className="text-xs text-font-secondary">
                  {c.vacationDaysPerYear} días · {hcount} feriados configurados
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setEditing(c)}>
                <Pencil size={12} /> Editar feriados
              </Button>
            </div>
          );
        })}
      </div>

      {editing && <HolidaysModal country={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function HolidaysModal({ country, onClose }: { country: any; onClose: () => void }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = useHolidays(country.id, year);
  const create = useCreateHoliday();
  const del = useDeleteHoliday();
  const [form, setForm] = useState({
    date: '',
    name: '',
    type: 'OFFICIAL',
    recurring: false,
  });

  const add = async () => {
    if (!form.date || !form.name) return;
    await create.mutateAsync({
      countryId: country.id,
      date: form.date,
      name: form.name,
      type: form.type,
      recurring: form.recurring,
    });
    setForm({ date: '', name: '', type: 'OFFICIAL', recurring: false });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="anim-scale-in max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-lg">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-font-primary">
              Feriados · {country.nameEs}
            </h3>
            <p className="text-xs text-font-secondary">
              Gestiona los días no laborables para {country.code}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-border bg-white px-2 text-sm"
            >
              {[year - 1, year, year + 1, year + 2].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-2 lg:grid-cols-12">
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="input h-10 lg:col-span-3"
          />
          <input
            placeholder="Nombre del feriado"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input h-10 lg:col-span-4"
          />
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="input h-10 lg:col-span-2"
          >
            <option value="OFFICIAL">Oficial</option>
            <option value="REGIONAL">Regional</option>
            <option value="COMPANY">Empresa</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-font-secondary lg:col-span-2">
            <input
              type="checkbox"
              checked={form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
            />
            Recurrente
          </label>
          <Button
            onClick={add}
            disabled={create.isPending}
            className="lg:col-span-1"
            aria-label="Agregar feriado"
          >
            <Plus size={14} />
          </Button>
        </div>

        <div className="max-h-[400px] overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                <th className="py-2">Fecha</th>
                <th className="py-2">Nombre</th>
                <th className="py-2">Tipo</th>
                <th className="py-2">Recurrente</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((h: any) => (
                <tr key={h.id} className="border-b border-border-light">
                  <td className="py-2 font-mono text-xs text-font-primary">
                    {new Date(h.date).toLocaleDateString('es')}
                  </td>
                  <td className="py-2 text-font-primary">{h.name}</td>
                  <td className="py-2">
                    <Badge
                      variant={
                        h.type === 'OFFICIAL'
                          ? 'info'
                          : h.type === 'REGIONAL'
                            ? 'neutral'
                            : 'default'
                      }
                    >
                      {h.type}
                    </Badge>
                  </td>
                  <td className="py-2">
                    <Badge variant={h.recurring ? 'success' : 'neutral'} dot>
                      {h.recurring ? 'Sí' : 'No'}
                    </Badge>
                  </td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => {
                        if (confirm('¿Eliminar feriado?')) del.mutate(h.id);
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-error transition hover:bg-red-50"
                      aria-label="Eliminar feriado"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {(data?.items?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-sm text-font-tertiary">
                    Sin feriados registrados para {year}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RolesSection() {
  const { data } = useUsers();
  const update = useUpdateUserRole();
  const reset = useResetUserPassword();
  const myRole = useAuthStore((s) => s.user?.role);
  const isAdmin = myRole === 'ADMIN';
  // Solo el ADMINISTRADOR puede reiniciar contraseñas.
  const canResetPasswords = isAdmin;
  const canResetTarget = (_targetRole: string) => isAdmin;
  const [resetResult, setResetResult] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [pendingResetId, setPendingResetId] = useState<string | null>(null);

  const handleReset = async (u: any) => {
    if (
      !confirm(
        `¿Reiniciar la contraseña de ${u.email}?\n\nSe generará una contraseña temporal y se cerrarán todas sus sesiones activas. El colaborador deberá cambiarla en su primer inicio de sesión. La nueva contraseña se mostrará una sola vez para que se la entregues.`,
      )
    )
      return;
    try {
      setPendingResetId(u.id);
      const res = await reset.mutateAsync(u.id);
      setResetResult({ email: res.email, password: res.password });
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'No se pudo reiniciar la contraseña');
    } finally {
      setPendingResetId(null);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-font-primary">Roles y Permisos</h2>
        <p className="mt-1 text-xs text-font-secondary">
          Asigna el rol del sistema a cada usuario. ADMIN tiene control total, HR gestiona
          colaboradores, MANAGER aprueba permisos, EMPLOYEE solo accede a su perfil.
        </p>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
            <th className="py-3">Usuario</th>
            <th className="py-3">Rol actual</th>
            <th className="py-3">Cambiar rol</th>
            {canResetPasswords && <th className="py-3 text-right">Contraseña</th>}
          </tr>
        </thead>
        <tbody>
          {data?.items?.map((u: any) => (
            <tr key={u.id} className="border-b border-border-light">
              <td className="py-3 text-font-primary">
                <div className="font-semibold">{u.email}</div>
                <div className="text-xs text-font-tertiary">
                  {u.employee
                    ? `${u.employee.firstName} ${u.employee.lastName}`
                    : 'Sin colaborador'}
                </div>
              </td>
              <td className="py-3">
                <Badge variant={u.role === 'ADMIN' ? 'info' : 'default'} dot>
                  {u.role}
                </Badge>
              </td>
              <td className="py-3">
                <select
                  value={u.role}
                  disabled={!isAdmin}
                  onChange={(e) => update.mutate({ id: u.id, role: e.target.value })}
                  title={
                    isAdmin
                      ? undefined
                      : 'Solo un administrador puede cambiar roles'
                  }
                  className="h-9 rounded-md border border-border bg-white px-2 text-sm disabled:cursor-not-allowed disabled:bg-bg disabled:text-font-tertiary"
                >
                  <option value="ADMIN">ADMIN</option>
                  <option value="HR">HR</option>
                  <option value="MANAGER">MANAGER</option>
                  <option value="EMPLOYEE">EMPLOYEE</option>
                </select>
              </td>
              {canResetPasswords && (
                <td className="py-3 text-right">
                  {canResetTarget(u.role) ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleReset(u)}
                      disabled={pendingResetId === u.id}
                      title="Generar una contraseña temporal para este usuario"
                    >
                      <KeyRound size={13} />
                      {pendingResetId === u.id ? 'Reiniciando...' : 'Reiniciar contraseña'}
                    </Button>
                  ) : (
                    <span className="text-[11px] text-font-tertiary" title="Requiere un administrador">
                      —
                    </span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {resetResult && (
        <ResetPasswordModal
          email={resetResult.email}
          password={resetResult.password}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback silencioso: el usuario puede seleccionar manualmente
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="anim-scale-in max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-primary" />
            <h3 className="text-base font-semibold text-font-primary">
              Contraseña temporal generada
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-font-tertiary hover:text-font-primary"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <p className="text-sm text-font-secondary">
            Entrega esta contraseña al colaborador por un canal seguro. Solo se mostrará
            <strong className="text-font-primary"> una vez</strong>. En su primer inicio de
            sesión el sistema le pedirá actualizarla por una nueva.
          </p>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-font-tertiary">
              Usuario
            </p>
            <p className="mt-1 text-sm font-medium text-font-primary">{email}</p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-font-tertiary">
              Nueva contraseña
            </p>
            <div className="mt-1 flex items-stretch gap-2">
              <code className="flex-1 select-all rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm tracking-wider text-font-primary">
                {password}
              </code>
              <Button type="button" variant="secondary" size="sm" onClick={copy}>
                {copied ? (
                  <>
                    <Check size={14} className="text-accent" /> Copiado
                  </>
                ) : (
                  <>
                    <Copy size={14} /> Copiar
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-yellow-50 px-3 py-2 text-xs text-warning">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              Todas las sesiones activas de este usuario fueron cerradas. Tendrá que volver a
              iniciar sesión con esta contraseña.
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button type="button" onClick={onClose}>
            Listo
          </Button>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_POLICIES = [
  {
    key: 'vacation.default',
    name: 'Días de vacaciones anuales (default)',
    value: { days: 15 },
  },
  {
    key: 'vacation.carryOver',
    name: 'Días acumulables al año siguiente',
    value: { maxDays: 5 },
  },
  {
    key: 'leave.maxConsecutiveVacation',
    name: 'Máx. días consecutivos de vacaciones sugeridos',
    value: { days: 15 },
  },
  {
    key: 'leave.approvalTimeoutDays',
    name: 'Escalar permiso si no se aprueba en X días',
    value: { days: 3 },
  },
];

function PoliciesSection() {
  const { data } = usePolicies();
  const upsert = useUpsertPolicy();
  const del = useDeletePolicy();
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState({
    key: '',
    name: '',
    description: '',
    value: '',
  });

  const openEdit = (p: any) => {
    setEditing(p);
    setForm({
      key: p.key,
      name: p.name,
      description: p.description ?? '',
      value: JSON.stringify(p.value, null, 2),
    });
  };

  const openNew = () => {
    setEditing({});
    setForm({ key: '', name: '', description: '', value: '{\n  \n}' });
  };

  const save = async () => {
    let parsedValue: any;
    try {
      parsedValue = JSON.parse(form.value);
    } catch {
      alert('El valor debe ser JSON válido');
      return;
    }
    await upsert.mutateAsync({
      key: form.key,
      name: form.name,
      description: form.description || undefined,
      value: parsedValue,
      active: true,
    });
    setEditing(null);
  };

  const ensureDefaults = async () => {
    for (const p of DEFAULT_POLICIES) {
      await upsert.mutateAsync({ ...p, active: true });
    }
  };

  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-font-primary">
            Políticas de Vacaciones y Permisos
          </h2>
          <p className="mt-1 text-xs text-font-secondary">
            Parámetros globales del sistema. Los valores están en formato JSON.
          </p>
        </div>
        <div className="flex gap-2">
          {(data?.items?.length ?? 0) === 0 && (
            <Button variant="secondary" onClick={ensureDefaults}>
              Crear defaults
            </Button>
          )}
          <Button variant="gradient" onClick={openNew}>
            <Plus size={14} /> Nueva política
          </Button>
        </div>
      </div>

      <div className="stagger space-y-3">
        {data?.items?.map((p: any) => (
          <div
            key={p.id}
            className="lift rounded-xl border border-border p-4 hover:border-primary"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-font-primary">{p.name}</p>
                <p className="text-xs text-font-tertiary">{p.key}</p>
                {p.description && (
                  <p className="mt-1 text-xs text-font-secondary">{p.description}</p>
                )}
                <pre className="mt-2 overflow-x-auto rounded-md bg-bg p-2 text-xs text-font-primary">
                  {JSON.stringify(p.value, null, 2)}
                </pre>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => openEdit(p)}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-font-secondary transition hover:bg-bg"
                  aria-label="Editar política"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => {
                    if (confirm('¿Eliminar política?')) del.mutate(p.key);
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-error transition hover:bg-red-50"
                  aria-label="Eliminar política"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="anim-scale-in max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">
              {editing.id ? 'Editar política' : 'Nueva política'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-font-secondary">Clave (key)</label>
                <input
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                  disabled={!!editing.id}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Descripción</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Valor (JSON)</label>
                <textarea
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="input min-h-[120px] w-full font-mono text-xs"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button onClick={save}>Guardar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CatalogsSection() {
  const [cat, setCat] = useState<'dept' | 'pos'>('dept');
  const { data: depts } = useDepartments();
  const { data: positions } = usePositions();
  const createDept = useCreateDepartment();
  const delDept = useDeleteDepartment();
  const createPos = useCreatePosition();
  const delPos = useDeletePosition();
  const [newName, setNewName] = useState('');
  const [newLevel, setNewLevel] = useState(1);

  const handleAdd = async () => {
    if (!newName) return;
    if (cat === 'dept') {
      await createDept.mutateAsync({ name: newName });
    } else {
      await createPos.mutateAsync({ title: newName, level: newLevel });
    }
    setNewName('');
    setNewLevel(1);
  };

  return (
    <div className="rounded-xl border border-border bg-white shadow-sm">
      <div className="flex border-b border-border">
        {(['dept', 'pos'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setCat(k)}
            className={`relative px-6 py-4 text-sm font-semibold ${
              cat === k ? 'text-primary' : 'text-font-secondary'
            }`}
          >
            {k === 'dept' ? 'Departamentos' : 'Puestos'}
            {cat === k && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
            )}
          </button>
        ))}
      </div>

      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-font-secondary">
              {cat === 'dept' ? 'Nuevo departamento' : 'Nuevo puesto'}
            </label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={cat === 'dept' ? 'Ej. Recursos Humanos' : 'Ej. Analista Jr'}
              className="input h-10 w-full"
            />
          </div>
          {cat === 'pos' && (
            <div>
              <label className="text-xs text-font-secondary">Nivel</label>
              <input
                type="number"
                min={1}
                max={20}
                value={newLevel}
                onChange={(e) => setNewLevel(Number(e.target.value))}
                className="input h-10 w-24"
              />
            </div>
          )}
          <Button onClick={handleAdd}>
            <Plus size={14} /> Agregar
          </Button>
        </div>

        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
              <th className="py-3">Nombre</th>
              <th className="py-3">{cat === 'dept' ? 'Padre' : 'Nivel'}</th>
              <th className="py-3">Detalle</th>
              <th className="py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(cat === 'dept' ? depts?.items : positions?.items)?.map((r: any) => (
              <tr key={r.id} className="border-b border-border-light">
                <td className="py-3 font-semibold text-font-primary">
                  {r.name ?? r.title}
                </td>
                <td className="py-3 text-font-secondary">
                  {cat === 'dept' ? r.parent?.name ?? '–' : `Nivel ${r.level}`}
                </td>
                <td className="py-3 text-font-secondary">
                  {cat === 'dept'
                    ? `${r._count?.employees ?? 0} colaboradores`
                    : r.description ?? '–'}
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => {
                      if (confirm('¿Desactivar este registro?')) {
                        if (cat === 'dept') delDept.mutate(r.id);
                        else delPos.mutate(r.id);
                      }
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-error transition hover:bg-red-50"
                    aria-label="Desactivar registro"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
